/* BillingAccounts and customers */

export interface BillingAccount {
    id: string;
    stripeId: string | null;

    name: string | null;
    description: string | null;
    email: string | null;
    phone: string | null;
    currency: string | null;
    defaultPaymentMethod: PaymentMethod | null;

    address: CustomerAddress | null;
    shipping: CustomerShipping | null;
}

export interface CustomerAddress {
    country: string;
    state: string;
    city: string;
    postalCode: string;
    line1: string;
    line2: string;
}

export interface CustomerShipping {
    name: string;
    phone: string | null;
    address: CustomerAddress;
}

export interface StripeSession {
    sessionId: string;
}

/* Prices and products */

export type PriceType = 'one_time' | 'recurring';

export interface Price {
    id: string;
    stripeId: string;

    active: boolean;
    currency: string;
    price: number;
    type: PriceType;

    tags: Record<string, string>;
}

export interface Product {
    id: string;
    stripeId: string;

    activeFrom: string;
    activeTo: string;
    owned: boolean;

    name: string;
    description: string;

    prices: Price[];
    tags: Record<string, string>;
}

export type OwnedProduct = Pick<Product, 'id' | 'stripeId' | 'name' | 'description'> & {
    active: boolean;
    currency: string;
    price: number;
    priceType: PriceType;
};

/* Checkout */

export interface CheckoutItem {
    priceId: string;
    quantity: number;
}

export type CheckoutSessionMode = 'payment' | 'subscription' | 'setup';

export interface CheckoutSessionParams {
    items?: CheckoutItem[];
    returnUrl?: string;
    mode?: CheckoutSessionMode;
    couponId?: string;
    /** Used when mode is "setup" */
    subscriptionId?: string;
}

/* Subscriptions and invoices */

export enum PaymentCardBrand {
    AmericanExpress = 'amex',
    DinersClub = 'diners',
    Discover = 'discover',
    JCB = 'jcb',
    Mastercard = 'mastercard',
    Visa = 'visa',
    UnionPay = 'unionpay',
    Unknown = 'unknown',
}

export interface PaymentMethodCard {
    brand: PaymentCardBrand;
    last4: string;
    expMonth: number;
    expYear: number;
}

export interface PaymentMethod {
    id: string;
    card: PaymentMethodCard | null;
    billingDetails: {
        name: string;
        email: string;
    } | null;
}

export interface Subscription {
    id: string;

    expiresAt: Date;
    status: string;
    cancelAtPeriodEnd: boolean;

    product: Omit<Product, 'tags'> & {
        tags: {
            key: string;
            value: string;
        }[];
    };
    paymentMethod: PaymentMethod | null;
}

export enum InvoiceStatus {
    Draft = 'draft',
    Open = 'open',
    Paid = 'paid',
    Uncollectible = 'uncollectible',
    Void = 'void',
}

export interface Invoice {
    id: string;
    /** Unique invoice number */
    number: string;

    /** When invoice was/will be created */
    created: Date;
    /** Amount due in lowest denomination of currency */
    amountDue: number;
    /** Amount remaining in lowest denomination of currency */
    amountRemaining: number;
    currency: string;

    status: InvoiceStatus;
    nextPaymentAttempt: Date;

    customerEmail: string;
    customerName: string | null;
    customerPhone: string | null;
    customerAddress: CustomerAddress | null;
    customerShipping: CustomerShipping | null;

    itemDescriptions: string[];
    /** Url of invoice, null for upcoming invoices */
    hostedInvoiceUrl: string;
    /** Detail string about invoice */
    footer: string;
}

/* Purchases */

export interface Purchase {
    productName: string;
    productDescription: string | null;
    productImages: string[];

    price: number;
    currency: string;

    purchasedAt: Date;
    status: 'completed' | 'succeeded';
}

export interface PurchasesResponse {
    items: Purchase[];
}
